import type { Money } from "./application";

interface VerificationStep {
  applicant_id: number;
  application_id: number;
  index: number;
  name: string;
  verification_id: number;
  json_schema: object;
  data: Record<string, unknown>;
}

export enum VerificationStatus {
  Pending = "pending",
  Verified = "verified",
  Finished = "finished",
  Failed = "failed",
  FinishedWithWarnings = "finished_with_warnings",
  FinishedByOverridden = "finished_by_overridden",
  Started = "started",
  Canceled = "canceled",
  Submitted = "submitted",
  Processing = "processing",
  ManuallyApproved = "manually_approved",
  NoDataAvailable = "no_data_available",
  Skipped = "skipped",
  EmailSent = "email_sent",
  Completed = "completed"
}

export enum ExecutionModes {
  Automatic = "automatic",
  Manual = "manual",
  Conditionally = "conditionally"
}

export interface VerificationOverriddenBy {
  id?: number;
  name: string;
  at: string;
}

export enum RerunRequirement {
  DecisionHasBeenMade = "decision_has_been_made",
  Pass60DaysPeriod = "pass_60_days_period"
}

export interface VerificationRequestedBy {
  id: number;
  name: string;
  email: string;
}

// combined income stuff
export enum IncomeType {
  Employment = "employment",
  SelfEmployment = "self_employment",
  OtherIncome = "other",
  OfferLetter = "offer_letter",
  NoIncome = "no_income",
  // Housing assistance is pseudo income type
  HousingAssistance = "housing_assistance"
}

export enum IncomeStatus {
  Pending = "pending", // When income created --> initial status
  Processing = "processing", // when document is upload that needs to be processed. We scheduled sidekiq job || before document was uploaded to Ocrolus
  Submitted = "submitted", // when offer letter or HA --> success for these
  Failed = "failed", // failed for plaid/ocrolus/inscribe
  Deleted = "deleted", // never should come. When in AX customer deletes income. PX should never have it. Keeping just in case
  Verified = "verified", // success for plaid/ocrolus/inscribe
  VerifiedWithWarnings = "verified_with_warnings" // success for plaid/ocrolus/inscribe
}
export interface IncomeItem {
  amount?: Money;
  id: number;
  pay_period?: string; // Yevhenii said that we should do ENUM
  start_date?: string;
  status: IncomeStatus;
  type: IncomeType;
  additional_data?: {
    supervisor?: {
      first_name?: string;
      last_name?: string;
      email?: string;
      phone?: string;
    };
    description?: string;
    paid_from?: string;
    company_name?: string;
    job_title?: string;
  };
}

export enum VerifyMethodType {
  Bank = "bank",
  Payroll = "payroll",
  Paystub = "paystub",
  BankStatement = "bank_statement",
  W2 = "w2",
  OfferLetter = "offer_letter",
  // HousingAssistance is not a verify method, it is a pseudo income type
  HousingAssistance = "housing_assistance",
  BenefitsStatement = "benefits_statement",
  Documents = "documents" // Not a real verify method - is added on FE for FE-only usage. Stands as a title for group of document-oriented verifications: paystub, bank_statment, w2
}

export enum IncomeSourceStatus {
  Pending = "pending",
  Started = "started",
  Processing = "processing",
  Failed = "failed",
  Finished = "finished",
  Deleted = "deleted",
  FinishedWithWarnings = "finished_with_warnings",
  Submitted = "submitted",
  SubmittedWithWarnings = "submitted_with_warnings",
  Reset = "reset"
}

export interface IncomeSource {
  id: number;
  status: IncomeSourceStatus;
  type: Exclude<VerifyMethodType, VerifyMethodType.Documents>;
  income_id: number | null;
  has_available_items: boolean;
  items: {
    id: number;
    income_source_id: number;
    income_id: number | null;
    data: {
      employer?: {
        name?: string | null;
      };
      employment: {
        employer?: {
          name?: string | null;
        };
      };
      income_description?: string | null;
      end_date: string;
      start_date: string;
      net_monthly_income: Money;
      net_monthly_income_amount: number;
    };
  }[];
}

export enum VerificationStartRequirements {
  Phone = "phone",
  IdentityVerification = "identity_verification",
  OtherVerifications = "other_verifications"
}

export interface Verification {
  id: number;
  name: VerificationNames;
  status: VerificationStatus;
  editable: boolean;
  index: number;
  estimated_time: number;
  title: string;
  description: string;
  progress?: {
    total_steps: number;
    filled_steps: number;
  };
  requested_at?: string;
  created_at: string;
  completed_at?: string;
  updated_at: string;
  steps?: VerificationStep[];
  vendor?: DataVendors;
  settings?: {
    execution_mode?: ExecutionModes;
    provider?: string;
    selfie_check?: boolean;
    identity_abuse_check?: {
      stolen_identity_threshold?: number;
      synthetic_identity_threshold?: number;
    };
    prevent_apply_if_failed?: boolean;
  };
  schema?: {
    version: number;
  };
  summary?: {
    ssn_mismatched: boolean;
  };
  overridden_by: VerificationOverriddenBy | null;
  fee: Money;
  rerun_requirements: RerunRequirement[];
  is_rerun: boolean;
  requested_by: VerificationRequestedBy | null;
  landlord_has_sent_surveys: Record<
    string,
    { property_address_index: number; status: SurveyStatus }[]
  >;
  landlord_surveys_data?: Record<
    string,
    { index: number; data?: Record<string, string> }[]
  >;
  // combined income stuff
  income_sources?: IncomeSource[];
  incomes?: IncomeItem[];
  has_active_submission_disclosure_link?: boolean;
  bypass_document_verification_enabled?: boolean;
  start_requirements?: VerificationStartRequirements[];
  archived_at?: string;
}

export enum DataVendors {
  Cleara = "cleara",
  CIC = "cic",
  Certn = "certn",
  Plaid = "plaid",
  Persona = "persona",
  TransUnion = "transunion",
  Equifax = "equifax"
}

export enum CreditReportModels {
  VantageScore = "vantage_score_model",
  FicoScore = "fico_score_model",
  ResidentScore = "resident_score_model"
}

export enum AnimalScreeningReportVendors {
  PetScreening = "petscreening_service",
  OurPetPolicy = "ourpetpolicy"
}

export const CreditReportModelsNumbers = {
  [CreditReportModels.VantageScore]: "05402",
  [CreditReportModels.FicoScore]: "05206",
  [CreditReportModels.ResidentScore]: "00AP4"
};

export const CreditReportModelsNumbersToNames = {
  "05402": "Vantage Score 4",
  "05206": "FICO 9",
  //transUnion
  "00W18": "FICO 9",
  "001NN": "Vantage Score 4",
  "00AP4": "Resident Score"
};

export enum VerificationNames {
  CreditReport = "credit_report",
  EvictionReport = "eviction_report",
  HousingHistory = "housing_history",
  RentalVerification = "rental_verification",
  PremiumRentalHistory = "premium_rental_history",
  RentalApplication = "rental_application",
  IncomeVerification = "income_verification", // Legacy. Should never come since CombinedIncome release
  VerifiedIncomeVerification = "verified_income_verification", // Legacy. Should never come since CombinedIncome release
  IdentityVerification = "identity_verification",
  CriminalReport = "criminal_report",
  CanadianCriminalReport = "canadian_criminal_report",
  TermsAndConditions = "terms_and_conditions",
  SubmissionDisclosure = "submission_disclosure",
  PetScreeningReport = "pet_screening_report",
  CombinedIncome = "combined_income_verification",
  BoomFinanceVerification = "boom_finance_verification"
}

// Verifications which exist as branch of verification:
// i.e. CreditReport fully controls CountyWalkIn
export enum PseudoVerificationNames {
  CountyWalkIn = "county_walk_in"
}

export const DISCLOSURES: [VerificationNames, VerificationNames] = [
  VerificationNames.TermsAndConditions,
  VerificationNames.SubmissionDisclosure
];

export enum TimeUnit {
  Months = "months",
  Years = "years"
}

export interface TimeFrame {
  number: number;
  unit: TimeUnit;
}

export const enum SurveyStatus {
  OriginalSent = "original_sent",
  FirstNudgeSent = "first_nudge_sent",
  SecondNudgeSent = "second_nudge_sent",
  FinalNudgeSent = "final_nudge_sent",
  NoResponse = "no_response",
  Failed = "failed",
  Delivered = "delivered",
  Completed = "completed",
  Declined = "declined",
  Pending = "pending",
  Started = "started"
}

export enum FieldDisplaySetting {
  Hidden = "hidden",
  Optional = "optional",
  Required = "required"
}

export enum ReverifyRequestStatus {
  Pending = "pending",
  Cancelled = "cancelled",
  Completed = "completed"
}

export interface ReverifyRequestActor {
  id: number | null;
  type: string | null;
  name?: string | null;
}

export interface ReverifyRequest {
  id: string;
  status: ReverifyRequestStatus;
  request_type: "applicant_request";
  income_id: number | null;
  income_type?: string | null;
  methods: string[];
  created_at: string;
  updated_at: string;
  created_by?: ReverifyRequestActor;
  cancelled_by?: ReverifyRequestActor;
  completed_by?: ReverifyRequestActor;
}

export interface Timeframe {
  value: number;
  unit: "month" | "year";
}

interface DocumentVerification {
  allowed: boolean;
  timeframe: Timeframe;
}

export enum DocumentVerificationType {
  BankStatement = "bank_statement",
  Paystub = "paystub",
  W2 = "w2",
  BenefitsStatement = "benefits_statement"
}

export interface CombinedIncomeConfigs<T> {
  bank_allowed: boolean;
  payroll_allowed: boolean;
  [DocumentVerificationType.BankStatement]: DocumentVerification;
  [DocumentVerificationType.Paystub]: DocumentVerification;
  [DocumentVerificationType.W2]: DocumentVerification;
  [DocumentVerificationType.BenefitsStatement]: DocumentVerification;
  fields: T;
}

export type CombinedEmployment = CombinedIncomeConfigs<{
  job_title: FieldDisplaySetting;
  supervisor: {
    first_name: FieldDisplaySetting;
    last_name: FieldDisplaySetting;
    phone: FieldDisplaySetting;
    email: FieldDisplaySetting;
  };
}>;

export type CombinedSelfEmployment = CombinedIncomeConfigs<{
  job_title: FieldDisplaySetting;
}>;

interface CombinedOfferLetter {
  fields: {
    job_title: FieldDisplaySetting;
    supervisor: {
      first_name: FieldDisplaySetting;
      last_name: FieldDisplaySetting;
      phone: FieldDisplaySetting;
      email: FieldDisplaySetting;
    };
  };
}

export type CombinedOther = CombinedIncomeConfigs<undefined>;

export type CombinedIncomeVerification =
  | CombinedEmployment
  | CombinedSelfEmployment
  | CombinedOfferLetter
  | CombinedOther;

export interface CombinedIncomeVerificationSchema {
  // combined_income_verification_enabled: boolean;
  housing_assistance_program_enabled: boolean;
  housing_assistance_upload: FieldDisplaySetting;
  employment: CombinedEmployment;
  self_employment: CombinedSelfEmployment;
  offer_letter: CombinedOfferLetter;
  other: CombinedOther;
}
